<style scoped>
table,
th,
td {
    border: 1px solid black;
    border-collapse: collapse;
}

td.even {
    background-color: lightgray;
}

.monitoring-summary {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px 16px;
    background: #fafafa;
}

.monitoring-summary-item + .monitoring-summary-item {
    margin-top: 6px;
}

.monitoring-warehouse-select :deep(.vs__dropdown-toggle) {
    min-height: 42px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 6px;
}

.monitoring-warehouse-select :deep(.vs__selected-options) {
    padding: 2px 6px;
}

.monitoring-warehouse-select :deep(.vs__search) {
    margin: 0;
}
</style>

<template>
    <div>
        <vx-card class="p-4 mb-6">
            <div class="flex flex-wrap items-center justify-between mb-4">
                <div class="flex items-center ml-1">
                    <h4>Monitoring</h4>
                    <div style="margin-left: 10px">
                        <strong>Last updated at: {{ lastUpdated }}</strong>
                    </div>
                </div>
                <div class="flex items-center">
                    <vs-button
                        @click="refreshData"
                        class="btn mb-4 md:mb-0 mr-1"
                        icon="refresh"
                        :disabled="isLoading"
                        >Refresh</vs-button
                    >
                    <vs-button
                        @click="handleExport"
                        class="btn mb-4 md:mb-0 mr-1"
                        icon="import_export"
                        :disabled="isExporting"
                        >Export</vs-button
                    >
                </div>
            </div>

            <div class="flex flex-wrap items-end -mx-2 mb-4">
                <div class="w-full md:w-1/4 px-2 mb-4 md:mb-0">
                    <label class="vs-input--label">Select Warehouse</label>
                    <v-select
                        :key="warehouseSelectKey"
                        ref="warehouseSelect"
                        class="monitoring-warehouse-select"
                        v-model="warehouse_ids"
                        :options="warehouses"
                        :reduce="(item) => item.id"
                        label="name"
                        multiple
                        placeholder="All Warehouses"
                        :close-on-select="false"
                    >
                        <template v-slot:search="{ attributes, events }">
                            <input
                                class="vs__search"
                                v-bind="attributes"
                                v-on="events"
                                :placeholder="
                                    warehouse_ids.length ? '' : 'All Warehouses'
                                "
                            />
                        </template>
                    </v-select>
                </div>
                <div class="w-full md:w-1/5 px-2 mb-4 md:mb-0">
                    <label class="vs-input--label">Start Date</label>
                    <flat-pickr
                        class="w-full p-2 vx-col"
                        v-model="start_date"
                    />
                </div>
                <div class="w-full md:w-1/5 px-2 mb-4 md:mb-0">
                    <label class="vs-input--label">End Date</label>
                    <flat-pickr
                        class="w-full p-2 vx-col"
                        v-model="end_date"
                    />
                </div>
                <div class="w-full md:w-auto px-2">
                    <div class="flex flex-wrap">
                        <vs-button
                            class="btn w-full md:w-auto mr-0 md:mr-1 mb-2 md:mb-0"
                            icon="filter_list"
                            @click="applyFilters"
                        >
                            Filter
                        </vs-button>
                        <vs-button
                            class="btn w-full md:w-auto"
                            color="warning"
                            icon="refresh"
                            @click="resetFilters"
                        >
                            Reset
                        </vs-button>
                    </div>
                </div>
            </div>

            <div class="monitoring-summary mb-4">
                <strong>Total Filtered Warehouses :</strong>
                <div v-if="totalPerProduct.length" class="mt-2">
                    <div
                        v-for="item in totalPerProduct"
                        :key="item.product_code"
                        class="monitoring-summary-item"
                    >
                        {{ item.product_code }} : {{ formatQty(item.total_inventory_qty) }}
                    </div>
                </div>
                <div v-else class="mt-2 text-muted">No data available</div>
            </div>

            <table class="table w-full border border-gray-300">
                <thead>
                    <tr>
                        <th class="text-center" rowspan="2">Tanggal</th>
                        <th class="text-center" rowspan="2">Warehouse</th>
                        <th class="text-center" rowspan="2">Product Code</th>
                        <th class="text-center" rowspan="2">Beginning QTY</th>
                        <th class="text-center" colspan="2">In QTY</th>
                        <th class="text-center" colspan="2">Out QTY</th>
                        <th class="text-center" rowspan="2">End QTY</th>
                    </tr>
                    <tr>
                        <th class="text-center">Inbound</th>
                        <th class="text-center">TWH In</th>
                        <th class="text-center">Outbound</th>
                        <th class="text-center">TWH Out</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(group, index) in tableData">
                        <tr
                            :key="`index-${index}-${trIndex}`"
                            v-for="(tr, trIndex) in group"
                        >
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                                :rowspan="group.length"
                                v-if="trIndex == 0"
                            >
                                {{ group[0].date }}
                            </td>
                            <td
                                class="text-center"
                                :rowspan="group.length"
                                :class="index % 2 === 0 ? 'even' : ''"
                                v-if="trIndex == 0"
                            >
                                {{ group[0].warehouse.name }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ tr.product.product_code }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.initial_qty) }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.inbound_qty) }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.twh_in_qty) }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.outbound_qty) }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.twh_out_qty) }}
                            </td>
                            <td
                                class="text-center"
                                :class="index % 2 === 0 ? 'even' : ''"
                            >
                                {{ formatQty(tr.inventory_qty) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>

            <vs-pagination
                class="mt-4"
                :total="totalPage"
                v-model="currentPage"
                @change="fetchData"
            ></vs-pagination>
        </vx-card>
    </div>
</template>

<script>
import FileSaver from 'file-saver'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import 'flatpickr/dist/flatpickr.css'
import 'vue-select/dist/vue-select.css'

export default {
    name: 'Monitoring',
    components: {
        flatPickr,
        vSelect,
    },
    data() {
        return {
            isLoading: false,
            isExporting: false,
            warehouse_ids: [],
            warehouseSelectKey: 0,
            start_date: '',
            end_date: '',
            warehouses: [],
            tableData: [],
            totalPerProduct: [],
            currentPage: 1,
            totalPage: 1,
            limit: 10,
            loadingCount: 0,
            lastUpdated: '-',
        }
    },
    watch: {
        isLoading: function () {
            if (this.isLoading) {
                this.$vs.loading()
            } else {
                this.$vs.loading.close()
            }
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        formatQty(value) {
            if (value === null || value === undefined || value === '') {
                return value
            }

            return Number(value).toLocaleString('id-ID')
        },
        setLoading(isStart) {
            if (isStart) {
                this.loadingCount += 1
            } else {
                this.loadingCount = Math.max(0, this.loadingCount - 1)
            }
            this.isLoading = this.loadingCount > 0
        },
        applyFilters() {
            this.currentPage = 1
            this.fetchData()
        },
        getExportWarehouseName() {
            if (!this.warehouse_ids.length) {
                return 'All Warehouses'
            }

            if (this.warehouse_ids.length > 1) {
                return 'Multiple Warehouses'
            }

            const selectedWarehouse = this.warehouses.find(
                (warehouse) =>
                    String(warehouse.id) === String(this.warehouse_ids[0])
            )

            return selectedWarehouse
                ? selectedWarehouse.name
                : this.warehouse_ids[0]
        },
        resetFilters() {
            this.warehouse_ids.splice(0, this.warehouse_ids.length)
            this.start_date = ''
            this.end_date = ''
            this.currentPage = 1
            this.warehouseSelectKey += 1
            this.$nextTick(() => {
                if (this.$refs.warehouseSelect) {
                    this.$refs.warehouseSelect.clearSelection()
                }
            })
            this.fetchData()
        },
        async fetchData() {
            this.setLoading(true)
            try {
                const params = {
                    page: this.currentPage,
                    limit: this.limit,
                }

                if (this.warehouse_ids.length) {
                    params.warehouse_ids = this.warehouse_ids
                }

                if (this.start_date) {
                    params.start_date = this.start_date
                }

                if (this.end_date) {
                    params.end_date = this.end_date
                }

                const response = await this.$http.get(
                    '/tvd/monitoring/inventory',
                    { params }
                )

                this.tableData = response.data.data
                this.warehouses = response.data.warehouses
                this.totalPage = response.data.last_page
                this.lastUpdated = response.data.updated_at
                this.totalPerProduct = response.data.total_per_product || []
            } catch (error) {
                this.handleError(error)
            } finally {
                this.setLoading(false)
            }
        },
        async refreshData() {
            this.setLoading(true)
            try {
                const response = await this.$http.get(
                    '/tvd/monitoring/daily-base-qty'
                )
            } catch (error) {
                this.handleError(error)
            } finally {
                this.setLoading(false)
                this.fetchData()
            }
        },
        async handleExport() {
            this.isExporting = true
            this.setLoading(true)

            try {
                const params = {}

                if (this.warehouse_ids.length) {
                    params.warehouse_ids = this.warehouse_ids
                }

                if (this.start_date) {
                    params.start_date = this.start_date
                }

                if (this.end_date) {
                    params.end_date = this.end_date
                }

                const response = await this.$http.post(
                    '/tvd/monitoring/export-excel',
                    params,
                    { responseType: 'blob' }
                )

                const warehouseName = this.getExportWarehouseName()

                const dateStr = this.$moment().format('DDMMYYYY')
                const filename = `Monitoring_${warehouseName}_${dateStr}.xlsx`

                FileSaver.saveAs(response.data, filename)
            } catch (error) {
                this.handleError(error)
            } finally {
                this.isExporting = false
                this.setLoading(false)
            }
        },
    },
}
</script>
